import { v4 as uuidv4 } from 'uuid';
import { Plugin } from 'ckeditor5/src/core';
import { ClickObserver } from 'ckeditor5/src/engine';
import { Widget, toWidget } from 'ckeditor5/src/widget';
import SetIsOpenCommand from './setisopencommand';
import '../theme/file.css';
export default class FileEditing extends Plugin {
    static get requires() {
        return [Widget];
    }
    static get pluginName() {
        return 'FileEditing';
    }
    constructor(editor) {
        super(editor);
        editor.config.define('fileUpload', {
            downloadLinkRewriter: undefined,
            fileEditingDowncast: undefined
        });
    }
    init() {
        const editor = this.editor;
        const schema = editor.model.schema;
        const conversion = editor.conversion;
        const fileUploadConfig = editor.config.get('fileUpload');
        const downloadLinkRewriter = fileUploadConfig && fileUploadConfig.downloadLinkRewriter ?
            fileUploadConfig.downloadLinkRewriter : undefined;
        const fileEditingDowncast = fileUploadConfig && fileUploadConfig.fileEditingDowncast ?
            fileUploadConfig.fileEditingDowncast : undefined;
        const setIsOpenCommand = new SetIsOpenCommand(editor);
        editor.commands.add('setIsOpen', setIsOpenCommand);
        schema.register('file', {
            allowWhere: '$block',
            allowAttributes: [
                'fileId',
                'fileReferenceId',
                'fileName',
                'fileUrl',
                'fileIsOpen'
            ],
            isObject: true
        });
        schema.register('fileInnerWrapper', {
            allowIn: 'file'
        });
        schema.register('fileName', {
            allowIn: 'fileInnerWrapper',
            allowAttributes: ['name']
        });
        schema.register('fileActionButtonsContainer', {
            allowIn: 'fileInnerWrapper'
        });
        schema.register('downloadButton', {
            allowIn: 'fileActionButtonsContainer',
            allowAttributes: ['href']
        });
        schema.register('deleteButton', {
            allowIn: 'fileActionButtonsContainer'
        });
        // <file> and <fileInnerWrapper> convertors
        conversion.for('upcast').elementToElement({
            model: (viewElement, { writer }) => {
                const fileReferenceId = viewElement.getAttribute('data-file-reference-id') ||
                    viewElement.getAttribute('data-file-element-id') ||
                    uuidv4();
                const newProps = {};
                if (viewElement.getAttribute('data-file-id')) {
                    newProps.fileId = viewElement.getAttribute('data-file-id');
                }
                if (viewElement.getAttribute('data-file-url')) {
                    newProps.fileUrl = viewElement.getAttribute('data-file-url');
                }
                if (viewElement.getAttribute('data-file-name')) {
                    newProps.fileName = viewElement.getAttribute('data-file-name');
                }
                if (viewElement.getAttribute('data-file-is-open')) {
                    newProps.fileIsOpen = viewElement.getAttribute('data-file-is-open');
                }
                return writer.createElement('file', { fileReferenceId, ...newProps });
            },
            view: {
                name: 'div',
                classes: 'ck-file'
            }
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'file',
            view: (modelElement, { writer }) => {
                const div = writer.createContainerElement('div', { class: 'ck-file' });
                return div;
            }
        }).attributeToAttribute({
            view: 'data-file-reference-id', model: 'fileReferenceId'
        }).attributeToAttribute({
            view: 'data-file-element-id', model: 'fileReferenceId'
        }).attributeToAttribute({
            view: 'data-file-id', model: 'fileId'
        }).attributeToAttribute({
            view: 'data-file-url', model: 'fileUrl'
        }).attributeToAttribute({
            view: 'data-file-name', model: 'fileName'
        }).attributeToAttribute({
            view: 'data-file-is-open', model: 'fileIsOpen'
        });
        conversion.for('editingDowncast').elementToElement({
            model: {
                name: 'file',
                attributes: [
                    'fileId',
                    'fileReferenceId',
                    'fileName',
                    'fileUrl',
                    'fileIsOpen'
                ]
            },
            view: (modelElement, { writer }) => {
                const hasNewHtmlFormat = (modelElement.getAttribute('fileReferenceId') &&
                    modelElement.getAttribute('fileName'));
                if (hasNewHtmlFormat && fileEditingDowncast) {
                    return fileEditingDowncast(modelElement, writer, editor, toWidget);
                }
                let elements = [];
                if (hasNewHtmlFormat) {
                    elements.push(_createFileNameStructure(writer, modelElement.getAttribute('fileName')));
                    const buttonElements = [];
                    const href = modelElement.getAttribute('fileUrl');
                    buttonElements.push(_createDownloadLink(writer, href));
                    buttonElements.push(_createDeleteButton(undefined, writer));
                    elements.push(writer.createContainerElement('div', { class: 'ck-file-buttons-container' }, buttonElements));
                    elements = [writer.createContainerElement('div', { class: 'ck-file-inner-wrapper' }, elements)];
                }
                const div = writer.createContainerElement('div', { class: 'ck-file' }, elements);
                return toWidget(div, writer);
            }
        }).attributeToAttribute({
            view: 'data-file-reference-id', model: 'fileReferenceId'
        }).attributeToAttribute({
            view: 'data-file-element-id', model: 'fileReferenceId'
        }).attributeToAttribute({
            view: 'data-file-id', model: 'fileId'
        }).attributeToAttribute({
            view: 'data-file-url', model: 'fileUrl'
        }).attributeToAttribute({
            view: 'data-file-name', model: 'fileName'
        }).attributeToAttribute({
            view: 'data-file-is-open', model: 'fileIsOpen'
        });
        conversion.for('downcast').add(dispatcher => {
            dispatcher.on('attribute:fileUrl', (evt, data, conversionApi) => {
                const rootEl = conversionApi.mapper.toViewElement(data.item);
                // If dragging and dropping, ignore
                if (!rootEl ||
                    !rootEl.getChild(0) ||
                    !rootEl.getChild(0).getChild(1) ||
                    !rootEl.getChild(0).getChild(1).getChild(0)) {
                    return;
                }
                const anchorEl = rootEl.getChild(0).getChild(1).getChild(0);
                const hrefAttr = data.attributeNewValue;
                const context = {
                    fileReferenceId: rootEl._attrs.get('data-file-reference-id') ||
                        rootEl._attrs.get('data-file-element-id')
                };
                const href = downloadLinkRewriter ?
                    downloadLinkRewriter(hrefAttr, rootEl, context) :
                    hrefAttr;
                if (href) {
                    conversionApi.writer.setAttribute('href', href, anchorEl);
                }
                else {
                    conversionApi.consumable.consume(data.item, evt.name);
                }
            }, { priority: 'low' });
        });
        if (downloadLinkRewriter instanceof Function) {
            conversion.for('editingDowncast').add(dispatcher => {
                const converter = (_evt, data, conversionApi) => {
                    if (!conversionApi.consumable.consume(data.item, 'attribute:href:file')) {
                        return;
                    }
                    const anchorEl = conversionApi.mapper.toViewElement(data.item);
                    const hrefAttr = data.attributeNewValue;
                    const referenceEl = anchorEl.parent.parent.parent;
                    const context = {
                        fileReferenceId: (referenceEl.getAttribute('data-file-reference-id') ||
                            referenceEl.getAttribute('data-file-element-id'))
                    };
                    const href = downloadLinkRewriter ?
                        downloadLinkRewriter(hrefAttr, anchorEl, context) :
                        hrefAttr;
                    if (href) {
                        conversionApi.writer.setAttribute('href', href, anchorEl);
                    }
                    else {
                        conversionApi.consumable.consume(data.item, 'attribute:href:downloadButton');
                    }
                };
                dispatcher.on('attribute:href', converter);
            });
        }
        conversion.elementToElement({
            model: 'fileInnerWrapper',
            view: {
                name: 'div',
                classes: 'ck-file-inner-wrapper'
            }
        });
        // <fileName> convertors
        conversion.for('upcast').elementToElement({
            model: (viewElement, { writer }) => {
                const name = viewElement.getChild(0).data;
                return writer.createElement('fileName', { name });
            },
            view: {
                name: 'span',
                classes: 'ck-file-name'
            }
        });
        conversion.for('downcast').elementToStructure({
            model: 'fileName',
            view: (modelElement, { writer }) => _createFileNameStructure(writer, modelElement.getAttribute('name'))
        });
        // <fileActionButtonsContainer>, <downloadButton>, <deleteButton> convertors
        conversion.elementToElement({
            model: 'fileActionButtonsContainer',
            view: {
                name: 'div',
                classes: 'ck-file-buttons-container'
            }
        });
        conversion.for('upcast').elementToElement({
            model: 'downloadButton',
            view: {
                name: 'a',
                classes: 'ck-file-button-link'
            }
        }).attributeToAttribute({
            view: 'href',
            model: 'href'
        });
        conversion.for('downcast').elementToElement({
            model: 'downloadButton',
            view: (modelElement, { writer }) => _createDownloadLink(writer, modelElement.getAttribute('href'))
        }).attributeToAttribute({ model: 'href', view: 'href' });
        conversion.for('upcast').elementToElement({
            model: 'deleteButton',
            view: {
                name: 'button',
                classes: 'ck-file-button-delete'
            }
        });
        conversion.for('downcast').elementToElement({
            model: 'deleteButton',
            view: (modelElement, { writer }) => _createDeleteButton(modelElement, writer)
        });
        // onclick event handler for delete button
        const view = editor.editing.view;
        const viewDocument = view.document;
        const model = editor.model;
        view.addObserver(ClickObserver);
        editor.listenTo(viewDocument, 'click', (evt, data) => {
            let target = data.target;
            if (!target || !target.name) {
                return undefined;
            }
            if (target.name === 'svg') {
                target = target.parent;
            }
            const modelElement = editor.editing.mapper.toModelElement(target);
            if (modelElement && modelElement.name == 'deleteButton') {
                // Old format
                model.change(writer => {
                    writer.remove(modelElement.findAncestor('file'));
                });
            }
            else if (!modelElement && target.hasClass('ck-file-button-delete')) {
                // New (but non-react) format
                const fileEl = target.parent.parent.parent;
                const fileModelEl = editor.editing.mapper.toModelElement(fileEl);
                model.change(writer => {
                    writer.remove(fileModelEl);
                });
            }
        });
    }
}
function _createSVG(domDoc) {
    const el = domDoc.createElementNS('http://www.w3.org/2000/svg', 'svg');
    el.setAttribute('height', '16');
    el.setAttribute('width', '16');
    el.setAttribute('viewBox', '0 0 16 16');
    el.setAttribute('class', 'ck-icon');
    return el;
}
function _createFillerText(viewWriter) {
    // Adding an empty string to elements that are 'empty' prevents CKEditor5 from inserting `<br data-cke-filler="true" />`.
    // This is useful for elements such as the delete button and file download link which are deemed as empty since they do
    // not contain any text.
    // *See: https://ckeditor.com/docs/ckeditor5/latest/api/module_engine_view_filler.html
    return viewWriter.createText('');
}
function _createDownloadLink(viewWriter, href) {
    const downloadIcon = viewWriter.createUIElement('svg', null, function (domDoc) {
        const svg = _createSVG(domDoc);
        svg.setAttribute('viewBox', '0 0 512 512');
        // eslint-disable-next-line max-len
        svg.innerHTML = '<path d="M216 0h80c13.3 0 24 10.7 24 24v168h87.7c17.8 0 26.7 21.5 14.1 34.1L269.7 378.3c-7.5 7.5-19.8 7.5-27.3 0L90.1 226.1c-12.6-12.6-3.7-34.1 14.1-34.1H192V24c0-13.3 10.7-24 24-24zm296 376v112c0 13.3-10.7 24-24 24H24c-13.3 0-24-10.7-24-24V376c0-13.3 10.7-24 24-24h146.7l49 49c20.1 20.1 52.5 20.1 72.6 0l49-49H488c13.3 0 24 10.7 24 24zm-124 88c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20zm64 0c0-11-9-20-20-20s-20 9-20 20 9 20 20 20 20-9 20-20z"></path>';
        return svg;
    });
    const fillerText = _createFillerText(viewWriter);
    return viewWriter.createContainerElement('a', {
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'ck-file-button-link ck-tooltip',
        'title': 'Download',
    }, [fillerText, downloadIcon]);
}
function _createDeleteButton(_modelElement, viewWriter) {
    const deleteIcon = viewWriter.createUIElement('svg', null, function (domDoc) {
        const svg = _createSVG(domDoc);
        svg.setAttribute('viewBox', '0 0 448 512');
        // eslint-disable-next-line max-len
        svg.innerHTML = '<path d="M432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16zM53.2 467a48 48 0 0 0 47.9 45h245.8a48 48 0 0 0 47.9-45L416 128H32z"/>';
        return svg;
    });
    const fillerText = _createFillerText(viewWriter);
    return viewWriter.createContainerElement('button', {
        class: 'ck-file-button-delete ck-tooltip',
        'title': 'Delete',
    }, [fillerText, deleteIcon]);
}
function _createPaperclipIcon(viewWriter) {
    return viewWriter.createUIElement('svg', null, function (domDoc) {
        const svg = _createSVG(domDoc);
        svg.setAttribute('height', '1em');
        svg.removeAttribute('width');
        svg.setAttribute('viewBox', '0 0 384 512');

        // eslint-disable-next-line max-len
        svg.innerHTML = '<path d="M224 136V0H24C10.7 0 0 10.7 0 24v464c0 13.3 10.7 24 24 24h336c13.3 0 24-10.7 24-24V160H248c-13.2 0-24-10.8-24-24zm160-14.1v6.1H256V0h6.1c6.4 0 12.5 2.5 17 7l97.9 98c4.5 4.5 7 10.6 7 16.9z"/>';
        return svg;
    });
}
function _createFileNameStructure(viewWriter, fileName) {
    const icon = _createPaperclipIcon(viewWriter);
    const fileNameTextNode = viewWriter.createText(fileName);
    const span = viewWriter.createContainerElement('span', { class: 'ck-file-name' }, [fileNameTextNode]);
    return viewWriter.createContainerElement('div', { class: 'ck-file-name-container' }, [icon, span]);
}
